import styled from "@emotion/styled";
import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Loader } from "@livechat/design-system";
import { Alert, Button, Heading, SpacingToken, Text } from "@livechat/design-system-react-components";

import LinkStyledButton from "components/button/LinkStyledButton";
import Block from "components/layout/Block";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { getNewSubscriptionDefaultState } from "helpers/subscription";
import { useChatWidget } from "hooks/use-chat-widget";
import { getNonViewersCount, selectIsAdmin } from "store/entities/agents";
import { getBilling, getIsFetchingBilling } from "store/entities/billing";
import { CreditCardActions } from "store/entities/credit-card";
import { selectIsPastDue } from "store/entities/license";
import { ISubscription } from "store/entities/subscriptions";
import { getIsSavingCreditCard } from "store/views/credit-card";
import { withVar } from "themes/helpers";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-named-as-default
import EditInvoiceEmail from "../components/EditInvoiceEmail";
import { TaxDetailsProvider } from "../contexts/tax-details-context";
import { withRecurlyJs } from "../withRecurlyJs";

import { AccountDetailsCreditCardSection } from "./AccountDetailsCreditCardSection";
import {
  LoaderWrapper,
  StyledEditInvoiceEmailWrapper,
  StyledSectionText,
  StyledSectionTitle,
  StyledWrapper,
} from "./styles";

interface IProps {
  subscription: ISubscription;
}

const AccountDetailsContainer: FC<IProps> = ({ subscription }) => {
  const dispatch = useDispatch();
  const [isCardFormVisible, setIsCardFormVisible] = useState(false);

  const isFetchingBilling = useSelector(getIsFetchingBilling);
  const billing = useSelector(getBilling);
  const nonViewersCount = useSelector(getNonViewersCount);
  const isSavingCreditCard = useSelector(getIsSavingCreditCard);
  const { setEndChatCallback, setSessionVariables, openChatWidget } = useChatWidget();
  const isAdmin = useSelector(selectIsAdmin);
  const isPastDuePayment = useSelector(selectIsPastDue);

  useEffect(() => {
    trackEngagement("Account details: Section opened", EventPlace.AccountDetails);
  }, []);

  const handleSubscriptionCardFromSubmitSuccess = (tokenId: string): void => {
    dispatch(
      CreditCardActions.addCreditCard({
        token: tokenId,
        newSubscription: getNewSubscriptionDefaultState(subscription, billing, nonViewersCount),
      })
    );
  };

  const handleSupportContact = (): void => {
    trackEngagement("Account details: Clicked cancel link");
    setSessionVariables({ intent: "cancel" });
    openChatWidget();
    setEndChatCallback(() => {
      setSessionVariables({});
    });
  };

  const handleDisplayCardForm = (): void => {
    setIsCardFormVisible(true);
  };

  const handleHideCardForm = (): void => {
    setIsCardFormVisible(false);
  };

  if (isFetchingBilling) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  return (
    <TaxDetailsProvider defaultCountryCode={billing?.billingInfo?.country} defaultZipCode={billing?.billingInfo?.zip}>
      <StyledWrapper>
        {billing?.billingInfo && (
          <div>
            <StyledSectionTitle size="xs">Invoices recipient</StyledSectionTitle>
            <StyledEditInvoiceEmailWrapper>
              <EditInvoiceEmail email={billing.invoiceEmails[0]} />
            </StyledEditInvoiceEmailWrapper>
          </div>
        )}
        <div>
          <StyledSectionTitle size="xs">Billing details</StyledSectionTitle>
          <AccountDetailsCreditCardSection
            billing={billing}
            onSubscriptionCardFromSubmitSuccess={handleSubscriptionCardFromSubmitSuccess}
            isSavingCreditCard={isSavingCreditCard}
            isCardFormVisible={isCardFormVisible}
            onDisplayCardForm={handleDisplayCardForm}
            onHideCardForm={handleHideCardForm}
          />
        </div>
        {isPastDuePayment && isAdmin && !isCardFormVisible && (
          <Block maxWidth="724px" marginBottom={withVar(SpacingToken.Spacing9)}>
            <Alert kind="error">
              <AlertHeader size="xs">Payment needed to keep your account active</AlertHeader>
              <AlertText>Your transaction was declined. Please use a different card or contact your bank.</AlertText>
              <Button kind="high-contrast" onClick={handleDisplayCardForm}>
                Update billing details
              </Button>
            </Alert>
          </Block>
        )}
        <div>
          <StyledSectionTitle size="xs">Cancel account</StyledSectionTitle>
          <StyledSectionText>
            <Text>
              If you cancel, your HelpDesk license will remain active until the end of your current billing period.
              <br />
              You won’t be charged again, but we don’t provide refunds for payments already made.
            </Text>
          </StyledSectionText>
          <Block marginLeft="10px">
            <LinkStyledButton onClick={handleSupportContact}>
              Yes, I want to contact support to cancel my account.
            </LinkStyledButton>
          </Block>
        </div>
      </StyledWrapper>
    </TaxDetailsProvider>
  );
};

const AlertHeader = styled(Heading)`
  padding-bottom: var(${SpacingToken.Spacing2});
`;

const AlertText = styled(Text)`
  padding-bottom: var(${SpacingToken.Spacing3});
`;

export default withRecurlyJs(AccountDetailsContainer);
